/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { useReactiveVar } from '@apollo/client';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { currentProvisioningInfoVar } from '../../../apollo/reactiveVariables/ProvisioningVariables';
import { dataExportModalProps } from '../../../apollo/reactiveVariables/UIVariables';
import { Paths } from '../../../common/enums/Paths';
import ApprovalsListContext from '../../../context/ApprovalsListContext/ApprovalsListContext';
import { useSetListDataMutation } from '../../../data/client/mutations/SetListData.generated';
import { useSetModalDataMutation } from '../../../data/client/mutations/SetModalData.generated';
import { useListDataQuery } from '../../../data/client/queries/ListData.generated';
import {
    ApprovalRequestType,
    ListData,
    ModalComponentType,
    Principal,
    RequestType,
} from '../../../data/types.generated';
import { TeamsInitializationStatus } from '../../../hosts/IHost';
import {
    EntryPoint,
    LogCategory,
    PanelRegion,
    PanelType,
    ScenarioName,
    StatusName,
    StepName,
    SubWorkLoad,
} from '../../../logger';
import {
    MobileApprovalsAppListComponent,
    MobileApprovalsAppListComponentProps,
} from '../../../mobile/MobileApprovalsApp/MobileApprovalsAppListComponent';
import { DeepLinkType } from '../../../service/DeepLinkManager/IDeepLinkManager';
import { ApprovalProvisioningInfo, ApprovalProvisioningState, ProvisioningStatusClient } from '../../../service/gen';
import { GraphClient } from '../../../service/graph/graphClient';
import { newGuid } from '../../../utilities/AppUtils';
import { isEntryMenuEnabled } from '../../../utilities/ECSConfigUtils';
import {
    getApprovalIdFromSubEntityId,
    getApprovalTypeFromSubEntityId,
    getDataExportJobIdFromSubEntityId,
    getFlowEnvironmentIdFromSubEntityId,
    getShareLinkTemplateIdFromSubEntityId,
    getSubEntityNotificationContext,
} from '../../../utilities/SubEntityUtils';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { personalAppListTitle } from '../../../utilities/TitleText';
import { getGraphEndpoint } from '../../../utilities/Utils';
import { DataExportModalType } from '../../containers/DataExport/DataExportModal';
import { ApprovalsAppListComponent, ApprovalsAppListComponentProps } from './ApprovalsAppList.component';
import { ApprovalsAppListItem } from './ApprovalsAppListItem';

export const ApprovalsAppListContainer: React.FC = (): JSX.Element => {
    const host = getAppHost();
    const loc = host.getLocale();
    const logger = host.getLogger();
    const logCategory = LogCategory.PersonalAppList;
    const history = useHistory();
    const {
        data: { listData },
    } = useListDataQuery();
    const [setModalDataMutation] = useSetModalDataMutation();
    const [setListDataMutation] = useSetListDataMutation();
    const [profilePics, setProfilePics] = React.useState({});

    const [setModalData] = useSetModalDataMutation();

    const provisioningInfo = useReactiveVar<ApprovalProvisioningInfo>(currentProvisioningInfoVar);

    const userRole = listData?.userRole;
    const approvalType = listData?.approvalType;

    const title = personalAppListTitle(approvalType, userRole, loc);

    const { loading, error, fetchMoreItems, hasMoreItems, refetch, config, items } =
        React.useContext(ApprovalsListContext);

    const openTaskModule = (): void => {
        setModalData({
            variables: {
                isOpen: true,
                componentType: ModalComponentType.CreateTaskModule,
                componentProps: null,
            },
        });
    };

    const openMobileViewDetails = (
        approvalType?: ApprovalRequestType,
        approvalId?: string,
        flowEnvironment?: string,
        templateUrl?: string,
        dataPayloadUrl?: string
    ): void => {
        switch (approvalType) {
            case ApprovalRequestType.ESign:
            case ApprovalRequestType.Docusign:
            case ApprovalRequestType.Basic:
            case ApprovalRequestType.Syntex: {
                history.push({
                    pathname: Paths.BasePath + Paths.DetailsNavigation,
                    search: `?${new URLSearchParams({
                        approvalID: approvalId,
                        environmentId: flowEnvironment,
                        approvalType: approvalType.toString(),
                    })}`,
                });
                break;
            }
            case ApprovalRequestType.MsApprovals: {
                history.push({
                    pathname: Paths.BasePath + Paths.DetailsV2Navigation,
                    search: `?${new URLSearchParams({
                        templateUrl,
                        dataPayloadUrl,
                        approvalType,
                    })}`,
                });
                break;
            }
            default: {
                history.push({
                    pathname: Paths.BasePath + Paths.UnsupportedOnMobile,
                    search: `?${new URLSearchParams({
                        errorMessage: loc.strings.view_approval_on_desktop_or_web,
                    })}`,
                });
            }
        }
    };

    const openModal = React.useCallback(
        (
            approvalType: ApprovalRequestType,
            requestType: RequestType,
            approvalId?: string,
            flowEnvironment?: string,
            templateUrl?: string,
            dataPayloadUrl?: string
        ): void => {
            const isViewDetailsV1 =
                approvalType === ApprovalRequestType.Basic ||
                approvalType === ApprovalRequestType.ESign ||
                approvalType === ApprovalRequestType.Docusign ||
                approvalType === ApprovalRequestType.Syntex;

            if (isViewDetailsV1 && !approvalId) {
                return;
            }

            if (!isViewDetailsV1 && (!templateUrl || !dataPayloadUrl)) {
                return;
            }

            logger.logTraceInfo(
                logCategory,
                `Switching to Details page for approval ${approvalId}, in environment ${flowEnvironment}, \
                type: ${approvalType}, with adaptive card: ${!isViewDetailsV1}`
            );
            logger.markScenario(ScenarioName.PersonalApp, StepName.OpenViewDetailsModule, StatusName.NA, {
                data: {
                    ApprovalId: approvalId,
                    FlowEnvironemnt: flowEnvironment,
                    ApprovalType: approvalType,
                    RequestType: requestType,
                    AdaptiveCard: !isViewDetailsV1,
                },
            });

            logger.logUserBiAction(
                SubWorkLoad.AppInteraction,
                ScenarioName.PersonalApp,
                StepName.OpenViewDetailsModule,
                'click',
                'nav',
                'approvalListItem',
                'approvalList',
                PanelRegion.Main,
                PanelType.Approvals,
                {
                    ApprovalType: approvalType,
                    RequestType: requestType,
                    AdaptiveCard: !isViewDetailsV1,
                }
            );

            if (isViewDetailsV1) {
                setModalDataMutation({
                    variables: {
                        isOpen: true,
                        componentType: ModalComponentType.ViewDetails,
                        componentProps: {
                            approvalId,
                            flowEnvironment,
                            approvalType,
                        },
                    },
                });
            } else {
                setModalDataMutation({
                    variables: {
                        isOpen: true,
                        componentType: ModalComponentType.ViewDetailsV2,
                        componentProps: {
                            templateUrl,
                            dataPayloadUrl,
                            approvalType,
                        },
                    },
                });
            }
        },
        [logger, logCategory, setModalDataMutation]
    );

    const setListData = React.useCallback(
        (listData: Partial<ListData>): void => {
            setListDataMutation({
                variables: listData,
            });
        },
        [setListDataMutation]
    );

    const dataExportDeepLinkHandler = React.useCallback(
        (subEntityId: string): void => {
            const jobId = getDataExportJobIdFromSubEntityId(subEntityId);
            if (getAppHost().isMobile()) {
                history.push(Paths.BasePath + Paths.DataExportResult, { jobId, isOpen: true });
            } else {
                dataExportModalProps({
                    isOpen: true,
                    modalType: DataExportModalType.ViewResult,
                    modalProps: { jobId },
                });
            }
        },
        [history]
    );

    const shareLinkDeepLinkHandler = React.useCallback(
        (subEntityId: string): void => {
            const templateId = getShareLinkTemplateIdFromSubEntityId(subEntityId);
            const componentProps = {
                templateInstance: { Id: templateId },
                entryPoint: EntryPoint.PersonalApp,
            };

            if (getAppHost().isMobile()) {
                history.push(Paths.BasePath + Paths.CreateTemplateApproval, componentProps);
            } else {
                setModalDataMutation({
                    variables: {
                        isOpen: true,
                        componentType: ModalComponentType.CreateFromTemplateTaskModule,
                        componentProps,
                    },
                });
            }
        },
        [history]
    );

    // Handle deep links (if any)
    React.useEffect((): void => {
        // return if the deep link has been already consumed
        if (getAppHost().getDeepLinkManager().isDeepLinkFresh() === false) {
            return;
        }

        const deepLinkType: DeepLinkType = getAppHost().getDeepLinkManager().getDeepLinkType();

        const subEntityId = host.getSubEntityId();
        switch (deepLinkType) {
            case DeepLinkType.Notification:
                let flowEnvironmentId = getFlowEnvironmentIdFromSubEntityId(subEntityId);

                // If the flowEnvironment property is an empty string (there is no environment associated with the notification)
                // we need to cast it to 'null' for the Flow APIs to process it correctly
                if (flowEnvironmentId.length === 0) {
                    flowEnvironmentId = null;
                }

                const approvalId = getApprovalIdFromSubEntityId(subEntityId);
                const subEntityApprovalType = getApprovalTypeFromSubEntityId(subEntityId) ?? approvalType;
                const extensibilityNotificationContext = getSubEntityNotificationContext(subEntityId);
                const detailsUrl = extensibilityNotificationContext?.detailsUri;
                const templateUrl = extensibilityNotificationContext?.templateUri;

                logger.logTraceInfo(
                    logCategory,
                    `Opening deep-linked details page for ${subEntityApprovalType} approval.`
                );

                if (getAppHost().isMobile()) {
                    openMobileViewDetails(
                        subEntityApprovalType,
                        approvalId,
                        flowEnvironmentId,
                        templateUrl,
                        detailsUrl
                    );
                } else {
                    openModal(subEntityApprovalType, undefined, approvalId, flowEnvironmentId, templateUrl, detailsUrl);
                }

                setListData({
                    flowEnvironment: flowEnvironmentId,
                    approvalType: subEntityApprovalType,
                });

                break;
            case DeepLinkType.DataExportFailed:
            case DeepLinkType.DataExportSuccess:
                dataExportDeepLinkHandler(subEntityId);
                break;
            case DeepLinkType.vivaConnectionsNewApproval:
                if (getAppHost().getDeepLinkManager().getDeepLinkType() === DeepLinkType.vivaConnectionsNewApproval) {
                    if (getAppHost().isMobile()) {
                        if (isEntryMenuEnabled()) {
                            // Initialize create request menu scenario
                            logger.createScenario(ScenarioName.CreateRequestMenu, EntryPoint.AdaptiveCard);
                            history.push(Paths.BasePath + Paths.CreateRequestMenu, {
                                showNaviBar: true,
                            });
                        } else {
                            history.push(Paths.BasePath + Paths.CreateApproval);
                        }
                    } else {
                        openTaskModule();
                    }
                }
                break;
            case DeepLinkType.TemplateManagement: {
                history.push(Paths.BasePath + Paths.TemplateManagement);
                break;
            }
            case DeepLinkType.ShareLink: {
                shareLinkDeepLinkHandler(subEntityId);
                break;
            }
            case DeepLinkType.vivaConnectionsApprovalsList:
            case DeepLinkType.Empty:
                break;
            default:
                break;
        }

        // Set current deep link as used to avoid showing view details on every render.
        getAppHost().getDeepLinkManager().setDeepLinkUsed();
    }, []);

    React.useEffect((): void => {
        if (
            (!provisioningInfo &&
                error &&
                error.networkError &&
                'statusCode' in error.networkError &&
                (error.networkError.statusCode === 403 || error.networkError.statusCode === 409)) ||
            // Only compute provisioning status for basic approvals
            (!provisioningInfo && approvalType === ApprovalRequestType.Basic)
        ) {
            new ProvisioningStatusClient()
                .getProvisioningStatus(newGuid())
                .then((result: ApprovalProvisioningInfo): void => {
                    currentProvisioningInfoVar(result);
                    logger.logTraceInfo(
                        logCategory,
                        `Successfully fetched provisioning info for tenant ${host.getTenantId()}. Result: ${result}`
                    );
                    // TODO: temporary nonUItelemetry to debug provisioning status/results later on
                    // remove after stabilization
                    logger.markScenario(ScenarioName.PersonalApp, 'ProvisioningResult', '', {
                        data: {
                            ProvisioningResult: result.State ?? '',
                            ProvisioningMessage: result.Message ?? '',
                        },
                    });
                })
                .catch((error: any): void => {
                    currentProvisioningInfoVar({ State: ApprovalProvisioningState.Unknown });
                    logger.logTraceError(
                        logCategory,
                        `Failed to get provisioning state for tenant ${host.getTenantId()}`
                    );
                    logger.logTraceHttpException('GetProvisioningStatus', error.message, error.status);
                });
        }
    }, [error]);

    // re-try the data fetch query after an error
    const onRefetch = (): void => {
        refetch();
    };

    const graphClient = React.useMemo((): GraphClient => {
        return new GraphClient(getGraphEndpoint());
    }, []);

    React.useEffect((): void => {
        if (getAppHost().getEcsConfig().MicrosoftTeamApprovals.ClientFetchListProfilePics) {
            try {
                const uniqueUsers = getUniqueUsers(items);

                const cachedPics = {};
                uniqueUsers.forEach((id: string): void => {
                    const key = `${id}-photo`;
                    const value = sessionStorage.getItem(key);
                    if (value != null) {
                        cachedPics[id] = value;
                        uniqueUsers.delete(id);
                    }
                });

                const fetchPics = async (users: string[]): Promise<void> => {
                    if (users.length) {
                        const pics = await graphClient.getUserProfilePictures(users);
                        if (pics && Object.keys(pics).length) {
                            setProfilePics({ ...cachedPics, ...pics });
                            Object.keys(pics).forEach((id: string): void => {
                                sessionStorage.setItem(`${id}-photo`, pics[id]);
                            });
                        }
                    } else {
                        setProfilePics(cachedPics);
                    }
                };

                fetchPics(Array.from(uniqueUsers));
            } catch (error) {
                getAppHost()
                    .getLogger()
                    .logTraceException(
                        LogCategory.FetchListProfilePictures,
                        `Failed to retrieve profile pictures for list view. Message: ${error.message}`,
                        error
                    );
            }
        }
    }, [graphClient, items]);

    const getUniqueUsers = (listItems: ApprovalsAppListItem[]): Set<string> => {
        const facepileLimit = 5;
        const requesters = listItems.map((item: ApprovalsAppListItem): string => item.requestedBy.id);
        const approvers = listItems
            .map((item: ApprovalsAppListItem): string[] =>
                item.approvers.slice(0, facepileLimit).map((principal: Principal): string => principal.id)
            )
            .flat();
        return new Set([...requesters, ...approvers]);
    };

    const childProps: ApprovalsAppListComponentProps = {
        loading: loading || (approvalType === ApprovalRequestType.Basic && !provisioningInfo),
        hasMoreItems,
        title,
        items,
        error,
        listData,
        openModal,
        setListData,
        fetchMoreItems,
        provisioningInfo,
        config,
        onRefetch,
        profilePics,
    };

    const mobileChildProps: MobileApprovalsAppListComponentProps = {
        loading: loading || (approvalType === ApprovalRequestType.Basic && !provisioningInfo),
        hasMoreItems,
        title,
        items,
        error,
        listData,
        openMobileViewDetails,
        setListData,
        fetchMoreItems,
        provisioningInfo,
        config,
        onRefetch,
        profilePics,
    };

    React.useEffect((): void => {
        const host = getAppHost();
        const logger = getAppHost().getLogger();

        logger.logTraceInfo(LogCategory.AppBoot, 'Notifying Teams for init success in personal app');
        host.notifyTeamsInitialization(TeamsInitializationStatus.Success);

        logger.markScenario(ScenarioName.AppBoot, StepName.AppBootApprovalsAppListRendered, StatusName.NA);
    }, []);

    // Log a step list data is fetched and close the scenario
    React.useEffect((): void => {
        const logger = getAppHost().getLogger();

        if (!loading) {
            logger.markScenario(ScenarioName.AppBoot, StepName.AppBootApprovalsAppListDataRendered, StatusName.NA);
            logger.stopScenario(ScenarioName.AppBoot);
        }
    }, [loading]);

    return getAppHost().isMobile() ? (
        <MobileApprovalsAppListComponent {...mobileChildProps} />
    ) : (
        <ApprovalsAppListComponent {...childProps} />
    );
};
